<template>
  <div class="verba-calculo">
    <div class="verba-calculo__cabecalho">
      <span class="verba-calculo__kicker">{{ $t("CFP") }}</span>
      <strong>Classificação e cálculo</strong>
      <small
        >Como a verba é calculada, sobre qual base e onde incide (INSS, IRRF e
        FGTS, pela classificação do eSocial).</small
      >
    </div>

    <v-skeleton-loader v-if="carregando" type="article, list-item-two-line" />
    <v-alert v-else-if="erro" type="warning" color="orange darken-4" text dense>
      {{ erro }}
      <a href="#" @click.prevent="carregar">Tentar novamente</a>
    </v-alert>

    <template v-else-if="form">
      <v-alert
        v-for="(p, i) in original.pendencias || []"
        :key="'p' + i"
        type="warning"
        color="orange darken-4"
        text
        dense
        class="mb-2"
        >{{ p }}</v-alert
      >

      <v-row dense>
        <v-col cols="12" md="4">
          <span class="opacity-5 text-dark">Tipo no eSocial</span>
          <v-select
            v-model="form.tipo"
            :items="tiposRubrica"
            label="Tipo"
            :class="campo"
            dense
            flat
            filled
            solo
          />
        </v-col>
        <v-col cols="12" md="8">
          <span class="opacity-5 text-dark">Papel no cálculo</span>
          <v-autocomplete
            v-model="form.papel"
            :items="papeisAgrupados"
            label="Papel no cálculo"
            :class="campo"
            dense
            flat
            filled
            solo
            clearable
            @change="sugerirPeloPapel"
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="papelAtual && papelAtual.explicacao"
        type="info"
        color="blue darken-2"
        text
        dense
        class="mb-2"
      >
        {{ papelAtual.explicacao }}
      </v-alert>
      <v-alert
        v-if="form.tipo > 2 && !calculadaPeloSistema"
        type="info"
        color="blue darken-2"
        text
        dense
        class="mb-2"
      >
        Informativa: sai no demonstrativo e no recibo sem somar nos vencimentos
        nem nos descontos (o líquido não muda) e pode ser lançada só com a
        quantidade, para registrar uma situação. Ainda assim, com incidência de
        FGTS 11, 12 ou 21 ela entra na base do FGTS, e a dedutora (tipo 4) deduz
        do IRRF: para apenas informar, use o tipo 3 e FGTS 00.
      </v-alert>

      <template v-if="!calculadaPeloSistema">
        <div class="verba-calculo__secao">Como calcular</div>
        <v-row dense>
          <v-col cols="12" md="4">
            <span class="opacity-5 text-dark">Forma de cálculo</span>
            <v-select
              v-model="form.formaCalculo"
              :items="formas"
              item-value="valor"
              item-text="descricao"
              label="Forma de cálculo"
              :class="campo"
              :disabled="!!formaFixa"
              dense
              flat
              filled
              solo
              clearable
            />
          </v-col>
          <v-col
            v-if="['HORA', 'DIA', 'PERCENTUAL'].includes(form.formaCalculo)"
            cols="12"
            md="3"
          >
            <span class="opacity-5 text-dark">{{ rotuloPercentual }}</span>
            <CampoValorFolha
              v-model="form.percentual"
              :label="rotuloPercentual"
              prefixo=""
              sufixo="%"
              :dica="dicaPercentual || ''"
            />
          </v-col>
          <v-col
            v-if="['HORA', 'DIA'].includes(form.formaCalculo)"
            cols="12"
            md="3"
          >
            <span class="opacity-5 text-dark">Divisor</span>
            <v-select
              v-model="form.divisor"
              :items="divisoresFiltrados"
              item-value="valor"
              item-text="descricao"
              label="Divisor"
              :class="campo"
              dense
              flat
              filled
              solo
            />
          </v-col>
          <v-col v-if="form.divisor === 'HORAS_INFORMADAS'" cols="12" md="2">
            <span class="opacity-5 text-dark">Horas do divisor</span>
            <v-text-field
              v-model="form.divisorHoras"
              type="number"
              label="Horas"
              :class="campo"
              dense
              flat
              filled
              solo
            />
          </v-col>
          <v-col
            v-if="form.formaCalculo === 'QUANTIDADE_VALOR'"
            cols="12"
            md="3"
          >
            <span class="opacity-5 text-dark">Valor unitário</span>
            <CampoValorFolha
              v-model="form.valorUnitario"
              label="Valor unitário"
            />
          </v-col>
        </v-row>
        <div v-if="explicacao" class="forma-ajuda">
          <div class="forma-ajuda__formula">
            <span class="forma-ajuda__rotulo">Como calcula</span>
            <code>{{ explicacao.formula }}</code>
          </div>
          <div class="forma-ajuda__grade">
            <div>
              <span class="forma-ajuda__rotulo">No lançamento</span>
              {{ explicacao.lancamento }}
            </div>
            <div>
              <span class="forma-ajuda__rotulo">Da verba</span>
              {{ explicacao.verba }}
            </div>
            <div>
              <span class="forma-ajuda__rotulo">Referência no recibo</span>
              {{ explicacao.referencia }}
            </div>
          </div>
          <div v-if="exemplo" class="forma-ajuda__exemplo">
            <span class="forma-ajuda__rotulo"
              >Exemplo com esta configuração</span
            >
            {{ exemplo }}
          </div>
          <div class="forma-ajuda__conferir">
            <span class="forma-ajuda__rotulo">Para conferir</span>
            <ul>
              <li v-for="(c, i) in explicacao.conferir" :key="i">{{ c }}</li>
            </ul>
          </div>
        </div>
        <v-expansion-panels flat class="forma-comparar mb-2">
          <v-expansion-panel>
            <v-expansion-panel-header class="px-0 py-1">
              Comparar as formas de cálculo
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="forma-comparar__nota">
                Em todas as formas, exceto Percentual, um valor em R$ informado
                no lançamento substitui a conta. Sem forma escolhida, vale a do
                papel no cálculo; sem papel, Valor informado.
              </p>
              <div class="forma-comparar__tabela">
                <table>
                  <thead>
                    <tr>
                      <th>Forma</th>
                      <th>Conta</th>
                      <th>Quando usar</th>
                      <th>Exemplo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="f in ajudaFormas"
                      :key="f.valor"
                      :class="{ atual: f.valor === form.formaCalculo }"
                    >
                      <td>{{ f.nome }}</td>
                      <td>
                        <code>{{ f.formula }}</code>
                      </td>
                      <td>{{ f.quando }}</td>
                      <td>{{ f.exemplo }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <template
          v-if="['HORA', 'DIA', 'PERCENTUAL'].includes(form.formaCalculo)"
        >
          <div class="verba-calculo__secao">Base de cálculo</div>
          <div class="d-flex flex-wrap">
            <v-checkbox
              v-model="form.baseSalario"
              label="Salário contratual"
              dense
              hide-details
              class="mr-6"
            />
            <v-checkbox
              v-model="form.baseSalarioMinimo"
              label="Salário mínimo"
              dense
              hide-details
              class="mr-6"
            />
            <v-checkbox
              v-model="form.proporcionalDias"
              label="Proporcional aos dias trabalhados"
              dense
              hide-details
            />
          </div>
          <span class="opacity-5 text-dark d-block mt-3"
            >Somar também estas verbas à base</span
          >
          <v-autocomplete
            v-model="form.bases"
            :items="outrasVerbas"
            item-value="id"
            :item-text="(r) => `${r.codigo} - ${r.descricao}`"
            label="Verbas da base"
            multiple
            chips
            small-chips
            deletable-chips
            hint="Ex.: adicional de periculosidade e noturno na base da hora extra (Súmulas 132 e 60 do TST)."
            persistent-hint
            :class="campo"
            dense
            flat
            filled
            solo
          />
        </template>

        <div class="verba-calculo__secao">Médias de férias, 13º e rescisão</div>
        <p class="medias-verba__nota">
          Marque em quais médias esta rubrica entra quando o valor varia de um
          mês para outro. Cada média soma o que foi pago no período e divide
          pelos meses do período (mês sem a rubrica conta como zero). Valores
          iguais todo mês entram melhor como lançamento fixo marcado para
          férias, 13º e rescisão.
        </p>
        <v-alert
          v-if="sugestaoMedias"
          type="info"
          color="blue darken-2"
          text
          dense
          class="mb-2"
        >
          O papel "{{ papelAtual.descricao }}" costuma compor as três médias.
          <a href="#" @click.prevent="marcarMedias">Marcar as três</a>
        </v-alert>
        <div class="medias-verba__grade">
          <div
            v-for="t in tiposMedia"
            :key="t.campo"
            class="medias-verba__opcao"
            :class="{ 'medias-verba__opcao--marcada': form[t.campo] }"
          >
            <v-checkbox
              v-model="form[t.campo]"
              dense
              hide-details
              class="mt-0 pt-0"
            >
              <template v-slot:label>
                <strong class="medias-verba__titulo">{{ t.titulo }}</strong>
              </template>
            </v-checkbox>
            <dl class="medias-verba__detalhes">
              <dt>Entra em</dt>
              <dd>{{ t.onde }}</dd>
              <dt>Período</dt>
              <dd>{{ t.periodo }}</dd>
              <dt>Costuma entrar</dt>
              <dd>{{ t.exemplos }}</dd>
            </dl>
          </div>
        </div>

        <div class="verba-calculo__secao">Como a média é apurada</div>
        <v-radio-group
          v-model="form.mediaPorQuantidade"
          :disabled="!compoeAlgumaMedia"
          hide-details
          class="mt-0 pt-0"
        >
          <div class="medias-verba__grade">
            <div
              class="medias-verba__opcao"
              :class="{
                'medias-verba__opcao--marcada':
                  compoeAlgumaMedia && form.mediaPorQuantidade,
              }"
            >
              <v-radio :value="true">
                <template v-slot:label>
                  <strong class="medias-verba__titulo"
                    >Pela quantidade × valor atual</strong
                  >
                </template>
              </v-radio>
              <p class="medias-verba__texto">
                Soma as horas, dias ou unidades do período, divide pelos meses e
                multiplica pelo valor da hora, do dia ou da unidade na data do
                cálculo. Os reajustes de salário já entram na média. Recomendado
                para horas extras e adicionais por hora (Súmula 347 do TST).
              </p>
              <p
                v-if="form.mediaPorQuantidade && !formaPorQuantidade"
                class="medias-verba__aviso"
              >
                Exige a forma de cálculo por hora, por dia ou quantidade × valor
                unitário. Com a forma atual, a média sai pelo valor pago.
              </p>
            </div>
            <div
              class="medias-verba__opcao"
              :class="{
                'medias-verba__opcao--marcada':
                  compoeAlgumaMedia && !form.mediaPorQuantidade,
              }"
            >
              <v-radio :value="false">
                <template v-slot:label>
                  <strong class="medias-verba__titulo"
                    >Pelo valor pago (R$)</strong
                  >
                </template>
              </v-radio>
              <p class="medias-verba__texto">
                Soma os valores pagos no período e divide pelos meses. Para
                comissões, prêmios, gorjetas e valores informados.
              </p>
            </div>
          </div>
        </v-radio-group>
        <div v-if="compoeAlgumaMedia" class="forma-ajuda__exemplo mb-2">
          <span class="forma-ajuda__rotulo">Exemplo com esta configuração</span>
          {{ exemploMedia }}
        </div>
        <div v-else class="text-caption text--secondary mb-2">
          Marque ao menos uma média para escolher como ela é apurada.
        </div>
      </template>

      <div class="verba-calculo__secao">
        Incidências (definem as bases de INSS, IRRF e FGTS)
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <span class="opacity-5 text-dark"
            >Natureza (Tabela 03 do eSocial)</span
          >
          <v-autocomplete
            v-model="form.naturezaEsocial"
            :items="naturezas"
            label="Natureza"
            :class="campo"
            dense
            flat
            filled
            solo
            clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <span class="opacity-5 text-dark"
            >INSS (contribuição previdenciária)</span
          >
          <v-autocomplete
            v-model="form.codIncCP"
            :items="incCp"
            label="INSS"
            :class="campo"
            dense
            flat
            filled
            solo
            clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <span class="opacity-5 text-dark">IRRF</span>
          <v-autocomplete
            v-model="form.codIncIRRF"
            :items="incIrrf"
            label="IRRF"
            :class="campo"
            dense
            flat
            filled
            solo
            clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <span class="opacity-5 text-dark">FGTS</span>
          <v-autocomplete
            v-model="form.codIncFGTS"
            :items="incFgts"
            label="FGTS"
            :class="campo"
            dense
            flat
            filled
            solo
            clearable
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="divergePapel"
        type="warning"
        color="orange darken-4"
        text
        dense
      >
        Classificação diferente da usual para o papel "{{
          papelAtual.descricao
        }}" (natureza {{ papelAtual.natureza }}, INSS {{ papelAtual.codIncCP }},
        IRRF {{ papelAtual.codIncIRRF }}, FGTS {{ papelAtual.codIncFGTS }}).
        <a href="#" @click.prevent="aplicarPapel">Usar a classificação usual</a>
      </v-alert>

      <template v-if="envioEsocialAtivo">
        <div class="verba-calculo__secao">eSocial</div>
        <v-row dense>
          <v-col cols="12" md="4">
            <span class="opacity-5 text-dark"
              >Tabela de rubricas (ideTabRubr)</span
            >
            <v-text-field
              v-model="form.esocialTabela"
              label="Tabela"
              :disabled="original.registradaEsocial"
              :class="campo"
              dense
              flat
              filled
              solo
            />
          </v-col>
          <v-col v-if="!original.registradaEsocial" cols="12" md="4">
            <span class="opacity-5 text-dark">Início de validade</span>
            <CampoCompetencia
              :payload="inicioValidade"
              label="MM/AAAA"
              opcional
              @date="inicioValidade = $event"
            />
          </v-col>
          <v-col
            v-else
            cols="12"
            md="8"
            class="text-caption text--secondary d-flex align-center"
          >
            Registrada no eSocial desde
            {{ competencia(original.esocialInicioValidade) }}.
          </v-col>
        </v-row>
        <v-alert
          v-if="exigeVigencia"
          type="info"
          color="blue darken-2"
          outlined
          dense
        >
          <div class="mb-2 font-weight-medium">
            A classificação mudou e a verba já está no eSocial. A mudança:
          </div>
          <v-radio-group v-model="vigencia" hide-details class="mt-0">
            <v-radio
              :value="original.vigenciaSugerida"
              :label="`Vale a partir de ${competencia(
                original.vigenciaSugerida
              )} (as folhas já fechadas continuam como estão)`"
            />
            <v-radio
              value="CORRECAO"
              label="Corrige um erro desde o início (as folhas já fechadas precisam ser recalculadas com uma complementar)"
            />
          </v-radio-group>
        </v-alert>
      </template>
    </template>
  </div>
</template>
<script>
import FuncionarioFolhaPagamentoVerbaDataService from "@/services/pessoal/FuncionarioFolhaPagamentoVerbaDataService";
import folhaUi from "@/components/pessoal/folha-calculo/folhaUi";
import CampoValorFolha from "@/components/pessoal/folha-calculo/CampoValorFolha.vue";
import envioAtivo from "@/components/pessoal/esocial-envio/envioAtivo";

// Verba nova: vencimento sem papel, a ser classificada pelo usuário.
function novaVerba() {
  return {
    id: null,
    tipo: 1,
    papel: null,
    sistema: false,
    formaCalculo: null,
    percentual: null,
    divisor: null,
    divisorHoras: null,
    valorUnitario: null,
    baseSalario: false,
    baseSalarioMinimo: false,
    proporcionalDias: false,
    mediaPorQuantidade: false,
    bases: [],
    naturezaEsocial: null,
    codIncCP: null,
    codIncIRRF: null,
    codIncFGTS: null,
    mediaFerias: false,
    media13: false,
    mediaAviso: false,
    esocialTabela: null,
    esocialInicioValidade: null,
    pendencias: [],
    registradaEsocial: false,
    vigenciaSugerida: null,
  };
}

// Como o motor do CFP valoriza cada forma de cálculo (AvaliadorRubricas.valorar), para conferência e parametrização.
const AJUDA_FORMAS = [
  {
    valor: "VALOR_INFORMADO",
    nome: "Valor informado",
    formula: "valor = valor do lançamento",
    quando:
      "Gratificações, prêmios, comissões, descontos de convênio e valores apurados fora da folha.",
    exemplo: "Comissão de R$ 850,00 lançada no mês = R$ 850,00.",
    lancamento:
      "O valor em R$, no lançamento do mês ou no lançamento fixo (que se repete até o fim da vigência).",
    verba: "Só a classificação: não usa base, percentual nem divisor.",
    referencia: "A quantidade lançada, quando houver.",
    conferir: [
      "Lançamento sem valor gera alerta e a verba sai zerada, exceto na informativa, que entra só com a quantidade.",
      "O valor não é recalculado: confira a origem (planilha, ponto, benefícios) antes de fechar.",
    ],
  },
  {
    valor: "HORA",
    nome: "Por hora",
    formula: "valor = horas × (base ÷ divisor) × percentual",
    quando:
      "Horas extras, adicional noturno, hora noturna reduzida, intrajornada e interjornada, sobreaviso.",
    exemplo:
      "Base R$ 2.200,00 ÷ 220 h = R$ 10,00 × 150% = R$ 15,00 por hora; 10 h = R$ 150,00.",
    lancamento:
      "A quantidade de horas em decimal (1h30 = 1,5), do ponto ou digitada.",
    verba:
      "Percentual (em branco, 100%), divisor e base de cálculo. A base usa o valor do mês cheio: faltas não reduzem o valor da hora.",
    referencia: "As horas, em horas e minutos (2,5 aparece como 02:30).",
    conferir: [
      "Valor da hora = base ÷ divisor. O divisor padrão de horas do funcionário vem do campo Divisor padrão de horas do cadastro do funcionário (em branco, 220), não da escala.",
      "Percentual 150 = hora + 50%; 200 = hora + 100%; 20 = só o adicional de 20% (noturno); 120 = hora noturna reduzida com o adicional de 20%.",
      "Inclua na base os adicionais que integram a hora (periculosidade e noturno na hora extra, Súmulas 132 e 60 do TST).",
      "Proporcional aos dias não muda o valor: as horas lançadas já definem a conta.",
    ],
  },
  {
    valor: "DIA",
    nome: "Por dia",
    formula: "valor = dias × (base ÷ 30 ou dias do mês) × percentual",
    quando: "Faltas, DSR, diárias calculadas sobre o salário.",
    exemplo:
      "Base R$ 3.000,00 ÷ 30 = R$ 100,00 por dia; 2 dias de falta = R$ 200,00.",
    lancamento: "A quantidade de dias.",
    verba:
      "Percentual (em branco, 100%), divisor (mês comercial ou civil) e base de cálculo, pelo valor do mês cheio.",
    referencia: "Os dias.",
    conferir: [
      "Mês comercial divide por 30 em qualquer mês; mês civil, pelos dias do mês da competência (28 a 31).",
      "Para descontos (faltas), o tipo da verba no eSocial deve ser desconto.",
      "Proporcional aos dias não muda o valor: os dias lançados já definem a conta.",
    ],
  },
  {
    valor: "PERCENTUAL",
    nome: "Percentual",
    formula: "valor = percentual × base (× dias pagos ÷ 30, se proporcional)",
    quando:
      "Insalubridade, periculosidade, adicional por tempo de serviço, gratificação de função em percentual.",
    exemplo:
      "Insalubridade 20% × salário mínimo R$ 1.621,00 = R$ 324,20; com 15 dias pagos e proporcional, R$ 162,10.",
    lancamento:
      "Nada obrigatório. Dias na quantidade calculam a fração (dias ÷ 30); o percentual do lançamento fixo substitui o da verba.",
    verba:
      "Percentual (obrigatório) e base de cálculo, pelos valores calculados na competência.",
    referencia: "O percentual.",
    conferir: [
      "Sem percentual na verba nem no lançamento, o demonstrativo fica com erro.",
      "Com Proporcional aos dias trabalhados, o valor acompanha os dias pagos no mês (admissão, afastamento, férias).",
      "Valor em R$ no lançamento não substitui a conta nesta forma.",
    ],
  },
  {
    valor: "QUANTIDADE_VALOR",
    nome: "Quantidade × valor unitário",
    formula: "valor = quantidade × valor unitário da verba",
    quando:
      "Refeições, quilometragem, peças produzidas, plantões com valor fixo por unidade.",
    exemplo: "22 refeições × R$ 1,50 = R$ 33,00.",
    lancamento: "A quantidade (unidades, refeições, quilômetros).",
    verba: "O valor unitário.",
    referencia: "A quantidade.",
    conferir: [
      "Sem valor unitário na verba, a verba sai zerada, com alerta.",
      "Um valor em R$ no lançamento substitui a conta.",
    ],
  },
];

// Onde cada indicador de média é usado pelo motor do CFP (CalculoMedias) e o período de apuração.
const TIPOS_MEDIA = [
  {
    campo: "mediaFerias",
    titulo: "Média de férias",
    onde: "Férias (gozo, 1/3 e abono), férias vencidas e proporcionais na rescisão e salário-maternidade (últimos 6 meses).",
    periodo:
      "Meses do período aquisitivo, até 12, divididos pelos meses do período (art. 142 da CLT); na rescisão, cada período usa os próprios meses. Comissões (papel Comissões): 12 meses anteriores ao início das férias ou ao desligamento (§ 3º).",
    exemplos:
      "Horas extras, adicional noturno, comissões, insalubridade ou periculosidade variáveis e o DSR sobre horas extras (OJ 394 do TST).",
  },
  {
    campo: "media13",
    titulo: "Média do 13º salário",
    onde: "1ª e 2ª parcelas, adiantamento nas férias e 13º proporcional na rescisão.",
    periodo:
      "Janeiro até o mês anterior ao pagamento (na 2ª parcela, até novembro), divididos pelos meses com 15 dias ou mais de trabalho, os mesmos dos avos (Decreto 10.854/2021, art. 77; Lei 4.090/1962, art. 1º, § 2º).",
    exemplos:
      "Horas extras (Súmula 45 do TST), adicional noturno, comissões, gorjetas e gratificação semestral (Súmula 253).",
  },
  {
    campo: "mediaAviso",
    titulo: "Média do aviso prévio",
    onde: "Aviso prévio indenizado e indenizações dos arts. 479 e 480 da CLT, na rescisão.",
    periodo: "Últimos 12 meses antes do desligamento (art. 487, § 3º, da CLT).",
    exemplos:
      "Horas extras habituais (art. 487, § 5º), adicional noturno e comissões. Gorjetas (Súmula 354) e gratificação semestral (Súmula 253) não entram.",
  },
];

const FORMAS_QUANTIDADE = ["HORA", "DIA", "QUANTIDADE_VALOR"];

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const CLASSIFICACAO = [
  "naturezaEsocial",
  "tipo",
  "codIncCP",
  "codIncIRRF",
  "codIncFGTS",
];

// Seção do cadastro da verba com a classificação eSocial e a configuração do cálculo (módulo CFP).
// Sem verbaId é a inclusão de uma verba nova. O formulário pai chama dados() na gravação.
export default {
  name: "ConfiguracaoCalculoVerba",
  components: { CampoValorFolha },
  mixins: [folhaUi, envioAtivo],
  props: {
    verbaId: { type: [Number, String], default: null },
  },
  data() {
    return {
      campo:
        "input-style font-size-input text-light-input placeholder-light input-icon",
      carregando: false,
      erro: null,
      original: {},
      form: null,
      catalogos: null,
      verbas: [],
      inicioValidade: null,
      vigencia: null,
    };
  },
  computed: {
    pronto() {
      return !!this.form && !this.carregando;
    },
    // Papéis calculados pelo próprio motor (salário, INSS, IRRF...) não têm forma de cálculo nem médias.
    calculadaPeloSistema() {
      return this.papelAtual ? !!this.papelAtual.sistema : !!this.form.sistema;
    },
    esocial() {
      return (this.catalogos && this.catalogos.esocial) || {};
    },
    tiposRubrica() {
      return (this.esocial.tiposRubrica || []).map((t) => ({
        value: Number(t.codigo),
        text: t.descricao,
      }));
    },
    naturezas() {
      return (this.esocial.naturezas || [])
        .filter((n) => !n.termino)
        .map((n) => ({ value: n.codigo, text: `${n.codigo} - ${n.nome}` }));
    },
    incCp() {
      return (this.esocial.incidenciasCp || []).map((n) => ({
        value: n.codigo,
        text: `${n.codigo} - ${n.descricao}`,
      }));
    },
    incIrrf() {
      return (this.esocial.incidenciasIrrf || [])
        .filter((n) => !n.termino)
        .map((n) => ({
          value: n.codigo,
          text: `${n.codigo} - ${n.descricao}`,
        }));
    },
    incFgts() {
      return (this.esocial.incidenciasFgts || []).map((n) => ({
        value: n.codigo,
        text: `${n.codigo} - ${n.descricao}`,
      }));
    },
    formas() {
      return (this.catalogos && this.catalogos.formas) || [];
    },
    divisoresFiltrados() {
      const todos = (this.catalogos && this.catalogos.divisores) || [];
      return this.form && this.form.formaCalculo === "DIA"
        ? todos.filter(
            (d) => d.valor === "MES_COMERCIAL" || d.valor === "MES_CIVIL"
          )
        : todos.filter(
            (d) =>
              d.valor === "HORAS_CONTRATUAIS" || d.valor === "HORAS_INFORMADAS"
          );
    },
    papeis() {
      return (this.catalogos && this.catalogos.papeis) || [];
    },
    papeisAgrupados() {
      const lista = [];
      let grupo = null;
      for (const p of this.papeis) {
        if (p.grupo !== grupo) {
          grupo = p.grupo;
          lista.push({ header: grupo });
        }
        lista.push({ value: p.valor, text: p.descricao });
      }
      return lista;
    },
    papelAtual() {
      return this.form
        ? this.papeis.find((p) => p.valor === this.form.papel)
        : null;
    },
    divergePapel() {
      const p = this.papelAtual,
        f = this.form;
      if (!p || !p.natureza || p.generico) return false;
      return (
        p.natureza !== f.naturezaEsocial ||
        p.codIncCP !== f.codIncCP ||
        p.codIncIRRF !== f.codIncIRRF ||
        p.codIncFGTS !== f.codIncFGTS
      );
    },
    // Papéis genéricos por hora ou por dia: a forma de cálculo é a do papel.
    formaFixa() {
      const p = this.papelAtual;
      return p && p.generico && ["HORA", "DIA"].includes(p.forma)
        ? p.forma
        : null;
    },
    outrasVerbas() {
      return this.verbas.filter(
        (r) => r.ativa && this.form && r.id !== this.form.id
      );
    },
    exigeVigencia() {
      if (
        !this.form ||
        !this.original.registradaEsocial ||
        !this.original.naturezaEsocial
      )
        return false;
      return CLASSIFICACAO.some(
        (c) => (this.form[c] || null) !== (this.original[c] || null)
      );
    },
    rotuloPercentual() {
      if (!this.form) return "";
      return {
        PERCENTUAL: "Percentual",
        HORA: "% do valor da hora",
        DIA: "% do valor do dia",
      }[this.form.formaCalculo];
    },
    dicaPercentual() {
      return this.form && this.form.formaCalculo !== "PERCENTUAL"
        ? "Ex.: 150 = hora extra 50% (hora + adicional); 200 = 100%; 20 = só o adicional noturno. Em branco = 100."
        : "";
    },
    ajudaFormas() {
      return AJUDA_FORMAS;
    },
    tiposMedia() {
      return TIPOS_MEDIA;
    },
    compoeAlgumaMedia() {
      return !!this.form && TIPOS_MEDIA.some((t) => this.form[t.campo]);
    },
    sugestaoMedias() {
      return (
        !!this.papelAtual &&
        this.papelAtual.compoeMedias &&
        !this.compoeAlgumaMedia
      );
    },
    formaPorQuantidade() {
      return FORMAS_QUANTIDADE.includes(this.formaEfetiva);
    },
    // Mesma conta do motor (CalculoMedias): 6 meses com a rubrica num período de 12.
    exemploMedia() {
      const moeda = (v) => BRL.format(v);
      if (this.form.mediaPorQuantidade && this.formaPorQuantidade) {
        const unidade =
          this.formaEfetiva === "HORA"
            ? ["10 h", "60 h", "5 h", "hora", 15]
            : this.formaEfetiva === "DIA"
            ? ["2 dias", "12 dias", "1 dia", "dia", 100]
            : ["10 unidades", "60 unidades", "5 unidades", "unidade", 2];
        return `${unidade[0]} em 6 dos 12 meses do período = ${
          unidade[1]
        } ÷ 12 = ${unidade[2]} × ${moeda(unidade[4])} (valor atual da ${
          unidade[3]
        }) = ${moeda(5 * unidade[4])} de média, somada à remuneração.`;
      }
      return `${moeda(600)} pagos em 6 dos 12 meses do período = ${moeda(
        3600
      )} ÷ 12 = ${moeda(300)} de média, somada à remuneração.`;
    },
    // Forma escolhida; sem forma, a do papel e, sem papel, valor informado (RubricaCalculo.formaEfetiva).
    formaEfetiva() {
      if (!this.form) return null;
      return (
        this.form.formaCalculo ||
        (this.papelAtual && this.papelAtual.forma) ||
        "VALOR_INFORMADO"
      );
    },
    explicacao() {
      return AJUDA_FORMAS.find((a) => a.valor === this.formaEfetiva) || null;
    },
    // Exemplo com o percentual, o divisor, a base e o valor unitário configurados (salário e salário mínimo de exemplo).
    exemplo() {
      const f = this.form;
      if (!f) return "";
      const forma = this.formaEfetiva;
      const pct = Number(f.percentual) || (forma === "PERCENTUAL" ? 0 : 100);
      const partes = [];
      let base = 0;
      if (f.baseSalario) {
        base += 2200;
        partes.push("salário R$ 2.200,00");
      }
      if (f.baseSalarioMinimo) {
        base += 1621;
        partes.push("salário mínimo R$ 1.621,00");
      }
      const outras = (f.bases || []).length;
      if (outras) partes.push(`${outras} verba(s) da base`);
      const rotuloBase = partes.length
        ? partes.join(" + ")
        : "sem base configurada";
      const moeda = (v) => BRL.format(v);
      if (["HORA", "DIA", "PERCENTUAL"].includes(forma) && !partes.length)
        return "Sem base de cálculo: marque o salário, o salário mínimo ou verbas da base. Sem base, a verba sai zerada, com alerta.";
      if (["HORA", "DIA", "PERCENTUAL"].includes(forma) && !base)
        return `A base é a soma das ${outras} verba(s) selecionadas${
          forma === "PERCENTUAL"
            ? ", pelos valores calculados na competência"
            : ", pelo valor do mês cheio"
        }; confira na memória de cálculo a base usada em cada demonstrativo.`;
      if (forma === "HORA") {
        const informado = f.divisor === "HORAS_INFORMADAS";
        const divisor = informado ? Number(f.divisorHoras) || 0 : 220;
        if (!divisor) return "Informe as horas do divisor.";
        const hora = (base / divisor) * (pct / 100);
        return `Base ${rotuloBase} ÷ ${divisor} h${
          informado
            ? ""
            : " (Divisor padrão de horas do cadastro do funcionário; em branco, 220)"
        } × ${pct}% = ${moeda(hora)} por hora; 10 h = ${moeda(hora * 10)}${
          outras ? " mais a parte das verbas da base" : ""
        }.`;
      }
      if (forma === "DIA") {
        const civil = f.divisor === "MES_CIVIL";
        const dias = civil ? 31 : 30;
        const dia = (base / dias) * (pct / 100);
        return `Base ${rotuloBase} ÷ ${
          civil ? "31 dias (outubro)" : "30 (mês comercial)"
        } × ${pct}% = ${moeda(dia)} por dia; 2 dias = ${moeda(dia * 2)}${
          outras ? " mais a parte das verbas da base" : ""
        }.`;
      }
      if (forma === "PERCENTUAL") {
        if (!pct)
          return "Informe o percentual: sem ele, o demonstrativo fica com erro.";
        const cheio = (base * pct) / 100;
        return `${pct}% × ${rotuloBase} = ${moeda(cheio)}${
          outras ? " mais a parte das verbas da base" : ""
        }${
          f.proporcionalDias
            ? `; com 15 dias pagos no mês, ${moeda(cheio / 2)}`
            : "; o valor é o mesmo em qualquer número de dias pagos"
        }.`;
      }
      if (forma === "QUANTIDADE_VALOR") {
        const unit = Number(f.valorUnitario) || 0;
        if (!unit)
          return "Informe o valor unitário: sem ele, a verba sai zerada, com alerta.";
        return `22 unidades lançadas × ${moeda(unit)} = ${moeda(unit * 22)}.`;
      }
      return "O lançamento de R$ 850,00 gera R$ 850,00.";
    },
  },
  watch: {
    verbaId: {
      immediate: true,
      handler() {
        this.carregar();
      },
    },
  },
  methods: {
    async carregar() {
      this.carregando = true;
      this.erro = null;
      try {
        const [v, c, l] = await Promise.all([
          this.verbaId
            ? FuncionarioFolhaPagamentoVerbaDataService.findCalculo(
                this.verbaId
              )
            : Promise.resolve({ data: novaVerba() }),
          this.catalogos
            ? Promise.resolve({ data: this.catalogos })
            : FuncionarioFolhaPagamentoVerbaDataService.getCatalogosCalculo(),
          FuncionarioFolhaPagamentoVerbaDataService.getCalculo(),
        ]);
        this.original = v.data;
        this.form = JSON.parse(JSON.stringify(v.data));
        // Sem escolha gravada, a média é pelo valor pago (como o motor faz).
        this.form.mediaPorQuantidade = !!this.form.mediaPorQuantidade;
        this.catalogos = c.data;
        this.verbas = l.data;
        this.inicioValidade = v.data.esocialInicioValidade
          ? v.data.esocialInicioValidade.substring(0, 7)
          : this.verbaId
          ? null
          : this.hoje().substring(0, 7);
        this.vigencia = v.data.vigenciaSugerida;
      } catch (e) {
        this.form = null;
        this.erro =
          (e.response && e.response.data && e.response.data.message) ||
          "Não foi possível carregar a configuração do cálculo.";
      } finally {
        this.carregando = false;
      }
    },
    sugerirPeloPapel() {
      const p = this.papelAtual,
        f = this.form;
      if (!p) return;
      if (!f.naturezaEsocial) this.aplicarPapel();
      if (!f.formaCalculo && p.forma) f.formaCalculo = p.forma;
      if (f.percentual == null && p.percentual != null)
        f.percentual = p.percentual;
      if (this.formaFixa) {
        f.formaCalculo = this.formaFixa;
        if (!this.divisoresFiltrados.some((d) => d.valor === f.divisor))
          f.divisor =
            this.formaFixa === "HORA" ? "HORAS_CONTRATUAIS" : "MES_COMERCIAL";
      }
    },
    marcarMedias() {
      for (const t of TIPOS_MEDIA) this.form[t.campo] = true;
      if (FORMAS_QUANTIDADE.includes(this.formaEfetiva))
        this.form.mediaPorQuantidade = true;
    },
    aplicarPapel() {
      const p = this.papelAtual,
        f = this.form;
      f.naturezaEsocial = p.natureza;
      f.codIncCP = p.codIncCP;
      f.codIncIRRF = p.codIncIRRF;
      f.codIncFGTS = p.codIncFGTS;
      if (p.tipo) f.tipo = p.tipo;
    },
    /** Configuração para gravar com a verba (PUT /funcionarioFolhaPagamentoVerbas/{id}/calculo). */
    dados() {
      const f = this.form;
      const vazio = (v) => (v === "" || v === undefined ? null : v);
      return {
        ...f,
        percentual: vazio(f.percentual),
        divisorHoras: vazio(f.divisorHoras),
        valorUnitario: vazio(f.valorUnitario),
        esocialInicioValidade: this.competenciaApi(this.inicioValidade),
        alteracaoVigencia: this.exigeVigencia ? this.vigencia : null,
      };
    },
  },
};
</script>
<style scoped>
.verba-calculo {
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  padding: 16px 18px;
}
.verba-calculo__cabecalho {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.verba-calculo__kicker {
  color: #5e72e4;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.verba-calculo__cabecalho strong {
  color: #202538;
  font-size: 1rem;
}
.verba-calculo__cabecalho small {
  color: #667085;
}
.verba-calculo__secao {
  color: #344054;
  font-size: 0.85rem;
  font-weight: 700;
  margin: 16px 0 8px;
}
.forma-ajuda {
  background: #f8f9fc;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  color: #344054;
  font-size: 0.82rem;
  margin: 4px 0 10px;
  padding: 12px 14px;
}
.forma-ajuda__rotulo {
  color: #667085;
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
  text-transform: uppercase;
}
.forma-ajuda code,
.forma-comparar code {
  background: #eef0fb;
  border-radius: 4px;
  color: #3b4bc8;
  font-size: 0.8rem;
  padding: 1px 6px;
}
.forma-ajuda__grade {
  display: grid;
  gap: 10px 18px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  margin-top: 10px;
}
.forma-ajuda__exemplo {
  border-top: 1px dashed #d0d5dd;
  margin-top: 10px;
  padding-top: 8px;
}
.forma-ajuda__conferir {
  margin-top: 10px;
}
.forma-ajuda__conferir ul {
  margin: 0;
  padding-left: 18px;
}
.forma-comparar {
  font-size: 0.82rem;
}
.forma-comparar .v-expansion-panel-header {
  color: #5e72e4;
  font-size: 0.82rem;
  font-weight: 700;
  min-height: 32px;
}
.forma-comparar__nota {
  color: #667085;
  margin-bottom: 8px;
}
.forma-comparar__tabela {
  overflow-x: auto;
}
.forma-comparar__tabela table {
  border-collapse: collapse;
  width: 100%;
}
.forma-comparar__tabela th,
.forma-comparar__tabela td {
  border-bottom: 1px solid #e4e7ec;
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
}
.forma-comparar__tabela th {
  color: #667085;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.forma-comparar__tabela tr.atual td {
  background: #f2f4ff;
}
.medias-verba__nota {
  color: #667085;
  font-size: 0.82rem;
  margin-bottom: 10px;
}
.medias-verba__grade {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  margin-bottom: 4px;
  width: 100%;
}
.medias-verba__opcao {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 10px 12px;
  transition: border-color 0.15s, background 0.15s;
}
.medias-verba__opcao--marcada {
  background: #f5f7ff;
  border-color: #5e72e4;
}
.medias-verba__titulo {
  color: #202538;
  font-size: 0.88rem;
}
.medias-verba__detalhes {
  font-size: 0.78rem;
  margin: 6px 0 0 32px;
}
.medias-verba__detalhes dt {
  color: #667085;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-top: 4px;
  text-transform: uppercase;
}
.medias-verba__detalhes dd {
  color: #344054;
  margin: 0;
}
.medias-verba__texto {
  color: #344054;
  font-size: 0.78rem;
  margin: 6px 0 0 32px;
}
.medias-verba__aviso {
  color: #b54708;
  font-size: 0.76rem;
  margin: 6px 0 0 32px;
}
</style>
